// @flow
import * as React from 'react';
import { getRoute } from '../router';
import {
  Users,
  Layout,
  UserSquare,
  Vibrate,
  UserList,
  Table,
  Files,
  Headset,
  ListChecks, PhoneIcon, UsersThreeIcon, SealCheck, CalendarIcon
} from '@phosphor-icons/react';
import { Group, Menu } from './menu';
import {
  getIsAdmin,
  getIsAgent,
  getIsCSR,
  keycloak,
  KeycloakRoles,
} from '@coverright/shared/keycloak';
import { useMissedAppsTagId, useMissingAppTotalQuery } from '../../shared';
import { EmailTemplateDrawerButton } from '../../features/email-template-drawer';
import { SearchAnythingButton } from '../../features/search-anything';
import { ComponentProps } from 'react';
import { RequiredActionsDrawerButton } from '../../features';

type ItemProps = ComponentProps<typeof Group>['items'];

export const getCsrMenuItems: () => {[key: string]: ItemProps} = () => ({
  Workspace: [{
    label: 'Dashboard',
    description: 'View clients dashboard',
    to: getRoute('dashboard'),
    icon: <Layout size={18} />
  }, {
    label: 'Clients',
    description: 'View clients',
    to: getRoute('clients'),
    icon: <Users size={18} />
  }, {
    label: 'Direct Mail',
    description: 'View direct mail leads',
    to: getRoute('directMail'),
    icon: <UsersThreeIcon size={18} />
  }, {
    label: 'Partner Leads',
    description: 'View partner leads',
    to: getRoute('partnerLeads'),
    icon: <UsersThreeIcon size={18} />
  }, {
    label: 'NerdWallet',
    description: 'View NerdWallet clients',
    to: getRoute('partnerLeads') + '/nerdWallet',
    secondary: true,
    icon: <Users size={18} />
  }, {
    label: 'Wegmans',
    description: 'View Wegmans clients',
    to: getRoute('partnerLeads') + '/wegmans',
    secondary: true,
    icon: <Users size={18} />
  }, {
    label: `Missing Apps`,
    description: 'View missing appointments - CSR follow up',
    to: getRoute('missingApps'),
    icon: <UserSquare size={18} />
  }, {
    label: '2 Hour SLA',
    description: 'View 2 Hour SLA report',
    to: getRoute('twoHourSla'),
    icon: <Vibrate size={18} />
  },  {
    label: '2 Hour SLA NW',
    description: 'View 2 Hour SLA report for NerdWallet clients',
    to: getRoute('twoHourSlaNW'),
    icon: <Vibrate size={18} />
  }, {
    label: 'Attempted Leads',
    description: 'View attempted leads',
    to: getRoute('attemptedLeads'),
    icon: <UserList size={18} />
  }, {
    label: 'Agent Rebook Requests',
    description: 'View agent rebook requests',
    to: getRoute('attemptedLeads'),
    icon: <UserList size={18} />
  }, {
    label: 'New Leads - Not Called',
    description: 'View not called leads',
    to: getRoute('attemptedLeads'),
    icon: <UserList size={18} />
  }, ],
})

export const getAgentMenuItems: () => {[key: string]: ItemProps} = () => ({
  Workspace: [{
    label: 'Dashboard',
    description: 'View clients dashboard',
    to: getRoute('dashboard'),
    icon: <Layout size={18} />
  }, {
    label: 'Clients',
    description: 'View clients',
    to: getRoute('clients'),
    icon: <Users size={18} />
  }],
  Tools: [{
    label: 'Email templates',
    description: 'Manage email templates',
    component: <EmailTemplateDrawerButton />,
    to: '',
    icon: null
  }, {
    label: 'Search',
    component: <SearchAnythingButton />,
    to: '',
    icon: null
  }]
})

export const getAgentManagerMenuItems: () => {[key: string]: ItemProps} = () => ({
  ...getAgentMenuItems(),
  'Management Tools': [{
    label: 'Round Robin',
    description: 'View Round Robin state',
    to: getRoute('roundRobin'),
    icon: <PhoneIcon size={18} />
  }, {
    label: 'Conversion Sync',
    description: 'View offline conversion sync status',
    to: getRoute('conversionSync'),
    icon: <Table size={18} />
  }, {
    label: 'Files',
    description: 'Manage plan files',
    to: getRoute('files'),
    icon: <Files size={18} />
  }, {
    label: 'Agents',
    description: 'Manage Agents',
    to: getRoute('agents'),
    icon: <Headset size={18} />
  }, {
    label: 'Agent Status Report',
    description: 'Agent Status Report',
    to: getRoute('salesManager'),
    icon: <Table size={18} />
  }, {
    label: 'CSR Tracking Report',
    description: 'View CSR tracking report',
    to: getRoute('csrTrackingReport'),
    icon: <Table size={18} />
  }],
})

export const getMainMenuItems: () => {[key: string]: ItemProps} = () => ({
  Workspace: [{
    label: 'Dashboard',
    description: 'View clients dashboard',
    to: getRoute('dashboard'),
    icon: <Layout size={18} />
  }, {
    label: 'Clients',
    description: 'View clients',
    to: getRoute('clients'),
    icon: <Users size={18} color="#1c434f" />
  }, {
    label: 'Calendar',
    description: 'View agent calendar',
    to: getRoute('calendar'),
    icon: <CalendarIcon size={18} color="#1c434f" />
  }, {
    label: 'Direct Mail',
    description: 'View direct mail leads',
    to: getRoute('directMail'),
    icon: <UsersThreeIcon size={18} color="#1c434f" />
  }, {
    label: 'Partner Leads',
    description: 'View partner leads',
    to: getRoute('partnerLeads'),
    icon: <UsersThreeIcon size={18} color="#1c434f" />
  }, {
    label: 'NerdWallet',
    description: 'View NerdWallet clients',
    to: getRoute('partnerLeads') + '/nerdWallet',
    secondary: true,
    icon: <Users size={18} color="#1c434f" />
  }, {
    label: 'Wegmans',
    description: 'View Wegmans clients',
    to: getRoute('partnerLeads') + '/wegmans',
    secondary: true,
    icon: <Users size={18} color="#1c434f" />
  }, {
    label: 'Next actions',
    description: 'Manage next actions',
    to: getRoute('actions'),
    icon: <ListChecks size={18} color="#1c434f" />
  }, {
    label: `Missing Apps`,
    description: 'View missing appointments - CSR follow up',
    to: getRoute('missingApps'),
    icon: <UserSquare size={18} color="#1c434f" />
  }, {
    label: 'QA & Compliance',
    description: 'AI compliance scores for calls',
    to: getRoute('qa'),
    icon: <SealCheck size={18} color="#1c434f" />
  }, {
    label: '2 Hour SLA',
    description: 'View 2 Hour SLA report',
    to: getRoute('twoHourSla'),
    icon: <Vibrate size={18} color="#1c434f" />
  },  {
    label: '2 Hour SLA NW',
    description: 'View 2 Hour SLA report for NerdWallet clients',
    to: getRoute('twoHourSlaNW'),
    icon: <Vibrate size={18} color="#1c434f" />
  }, {
    label: 'Attempted Leads',
    description: 'View attempted leads',
    to: getRoute('attemptedLeads'),
    icon: <UserList size={18} color="#1c434f" />
  }, {
    label: 'CSR Tracking Report',
    description: 'View CSR tracking report',
    to: getRoute('csrTrackingReport'),
    icon: <Table size={18} color="#1c434f" />
  }, {
    label: 'Round Robin',
    description: 'View Round Robin state',
    to: getRoute('roundRobin'),
    icon: <PhoneIcon size={18} color="#1c434f" />
  }, {
    label: 'Conversion Sync',
    description: 'View offline conversion sync status',
    to: getRoute('conversionSync'),
    icon: <Table size={18} color="#1c434f" />
  }],
  Admin: [{
    label: 'Files',
    description: 'Manage plan files',
    to: getRoute('files'),
    icon: <Files size={18} color="#1c434f" />
  }, {
    label: 'Agents',
    description: 'Manage Agents',
    to: getRoute('agents'),
    icon: <Headset size={18} color="#1c434f" />
  }, {
    label: 'Agent Status Report',
    description: 'Agent Status Report',
    to: getRoute('salesManager'),
    icon: <Table size={18} color="#1c434f" />
  }],
  Tools: [{
    label: 'Email templates',
    description: 'Manage email templates',
    component: <EmailTemplateDrawerButton />,
    to: '',
    icon: null
  }, {
    label: 'Search',
    component: <SearchAnythingButton />,
    to: '',
    icon: null
  }]
})

export const MainMenu = () => {
  const missingAppTagId = useMissedAppsTagId();
  const {data: missingAppTotalData} = useMissingAppTotalQuery({variables: {missingAppTagId}, skip: !missingAppTagId, pollInterval: 60000});
  const missingAppTotal = missingAppTotalData?.clientViews?.totalElements !== undefined ? `(${missingAppTotalData?.clientViews?.totalElements})` : '';

  const isAgentManager = keycloak?.hasRealmRole(KeycloakRoles.ROLE_AGENT_MANAGER)
  const menuItems = getMainMenuItems()

  if (!isAgentManager) {
    delete menuItems['Admin'];
    menuItems['Workspace'] = menuItems['Workspace'].filter(item => item.label !== 'Conversion Sync');
  }

  const index = menuItems['Workspace'].findIndex(item => item.label === 'Missing Apps');
  menuItems['Workspace'][index] = {
    ...menuItems['Workspace'][index],
    label: `Missing Apps ${missingAppTotal}`,
  };


  return <Menu>
    {/*!isCSR && <CallAgenda />*/}
    <RequiredActionsDrawerButton />
    {Object.keys(menuItems).map(key => (
      <Group title={key}
             key={key}
             items={menuItems[key]} />
    ))}
  </Menu>
};
